export interface MeetingParticipant {
  id: string;
  name: string;
  isHost: boolean;
  platform?: string;
  email?: string;
  joinedAt?: string;
  leftAt?: string;
  speakingTimeSeconds?: number;
}

export interface ActionItem {
  text: string;
  owners?: string[];
  due?: string;
  priority?: string;
  sequence: number;
}

export interface Decision {
  text: string;
  owners?: string[];
  sequence: number;
}

export interface Topic {
  name: string;
  sequence: number;
}

export interface KeyInsight {
  text: string;
  sentiment?: "positive" | "negative" | "neutral";
  sequence: number;
}

export interface Risk {
  text: string;
  severity?: "low" | "medium" | "high" | "critical";
  sequence: number;
}

export interface OpenQuestion {
  text: string;
  sequence: number;
}

// Upper bound on how long after a meeting ends we still treat its "ended" or
// "processing" status as an actively-running intelligence pipeline. Anything
// older is almost certainly stuck/failed — backend pipeline runs are minutes,
// not hours — and surfacing "Preparing notes" on a week-old meeting reads as
// a UI bug. The backend's auto_stop_stale_meetings job (15 min stale, 10 min
// cadence) and the typical Recall upload + transcribe + summarize chain fit
// comfortably inside this window with headroom.
export const PROCESSING_WINDOW_MS = 15 * 60 * 1000;

// True only when the backend status says the meeting is in the post-recording
// pipeline AND the meeting ended recently enough that the pipeline could
// plausibly still be running. Legacy meetings stuck in "ended" status from
// last week return false. Falls back to updatedAt when endTime is missing
// (the backend doesn't always persist endTime — see e2e-architecture.md).
export function isFreshlyProcessing(m: {
  status: string;
  endTime?: string;
  updatedAt?: string;
}): boolean {
  if (m.status !== "ended" && m.status !== "processing") return false;
  const anchor = m.endTime ?? m.updatedAt;
  if (!anchor) return false;
  return Date.now() - new Date(anchor).getTime() < PROCESSING_WINDOW_MS;
}

export interface MeetingRecord {
  id: string;
  externalMeetingId: string;
  recallMeetingId?: string;
  sessionId?: string;
  title?: string;
  platform?: string;
  startTime?: string;
  endTime?: string;
  meetingUrl?: string;
  transcriptText?: string;
  summaryText?: string;
  privateNotes?: string;
  // User edits to the AI-generated highlights, keyed by the original AI text.
  // Durable, cross-device home for what used to live only in localStorage.
  highlightEdits?: {
    summary?: string | null;
    items?: Record<string, string>;
  } | null;
  // Backend MeetingSession states: idle | recording | paused | ended | processing | done | error.
  // "completed" and "failed" remain in the union for legacy MeetingIntelligence
  // records that pre-date the session architecture (their pipeline used those
  // strings). All seven new states must be handled wherever this is read.
  status:
    | "scheduled"
    | "idle"
    | "recording"
    | "paused"
    | "ended"
    | "processing"
    | "done"
    | "error"
    | "completed"
    | "failed";
  participants: MeetingParticipant[];
  detailedSummary?: {
    summary?: string;
    actionItems: ActionItem[];
    decisions: Decision[];
    topics: Topic[];
    keyInsights: KeyInsight[];
    risks: Risk[];
    openQuestions: OpenQuestion[];
  };
  // Folder memberships (UUIDs of every folder this meeting is in). Always
  // [] for active sessions — assignment is only valid after intelligence
  // finalizes. See /v1/folders/ for folder metadata.
  folderIds?: string[];
  createdAt: string;
  updatedAt: string;
}
